/** TI inventory staff phone login — same pattern as KA kitchen staff. */

import {
  loadMirroredSessionToken,
  saveMirroredSessionToken,
  staffSessionApiError,
} from "@/lib/staffSessionStability";

export const INVENTORY_STAFF_TOKEN_KEY = "ttt_inventory_staff_token";
export const INVENTORY_STAFF_TOKEN_MIRROR_KEY = "ttt_inventory_staff_token_mirror";
export const INVENTORY_STAFF_PHONE_KEY = "ttt_inventory_staff_phone";

function parseApiError(data, fallback) {
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.loc?.join(".") || JSON.stringify(d)).join("; ");
  }
  return fallback;
}

export function loadInventoryStaffToken() {
  return loadMirroredSessionToken(INVENTORY_STAFF_TOKEN_KEY, INVENTORY_STAFF_TOKEN_MIRROR_KEY);
}

export function hasInventoryStaffSession() {
  return Boolean(loadInventoryStaffToken());
}

export function saveInventoryStaffToken(token) {
  saveMirroredSessionToken(INVENTORY_STAFF_TOKEN_KEY, INVENTORY_STAFF_TOKEN_MIRROR_KEY, token);
}

export function loadSavedInventoryStaffPhone() {
  try {
    return (localStorage.getItem(INVENTORY_STAFF_PHONE_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function saveInventoryStaffPhone(phone) {
  try {
    const clean = (phone || "").replace(/\D/g, "").slice(-10);
    if (clean) localStorage.setItem(INVENTORY_STAFF_PHONE_KEY, clean);
  } catch {
    /* ignore */
  }
}

export function clearInventoryStaffSession() {
  saveInventoryStaffToken("");
  try {
    localStorage.removeItem(INVENTORY_STAFF_PHONE_KEY);
  } catch {
    /* ignore */
  }
}

export async function registerInventoryStaff({ name, phone }) {
  const fd = new FormData();
  fd.append("name", name);
  fd.append("phone", phone);
  const res = await fetch("/api/inventory/staff/register", { method: "POST", body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseApiError(data, "Registration failed"));
  if (data.token) saveInventoryStaffToken(data.token);
  if (phone) saveInventoryStaffPhone(phone);
  return data;
}

export async function fetchInventoryStaffMe() {
  const token = loadInventoryStaffToken();
  if (!token) throw staffSessionApiError("Not logged in", 401);
  const params = new URLSearchParams({ token });
  const res = await fetch(`/api/inventory/staff/me?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw staffSessionApiError(parseApiError(data, "Could not load profile"), res.status);
  return data;
}

export async function tiFetch(path, options = {}) {
  const token = loadInventoryStaffToken();
  const sep = path.includes("?") ? "&" : "?";
  const url = `${path}${sep}token=${encodeURIComponent(token)}`;
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    saveInventoryStaffToken("");
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) throw new Error(parseApiError(data, "Request failed"));
  return data;
}

export async function tiPost(path, body) {
  const token = loadInventoryStaffToken();
  const payload = { ...body, token };
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    saveInventoryStaffToken("");
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) throw new Error(parseApiError(data, "Request failed"));
  return data;
}
